import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// World-owned theme inputs through the local server's grant door: the same
// worldThemeProfiles row the cockpit browser writes, so one World accent
// follows the Actor across doors. The browser names a World and two numbers;
// the Actor and the world.manage write gate live server-side in the grant.

export type WorldThemeOutcome =
  | {
      readonly ok: true;
      readonly accentHue: number;
      readonly accentChroma: number;
      readonly updatedAt: number | null;
    }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

function refusal(response: Response, body: Record<string, unknown> | null): WorldThemeOutcome {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string" ? body["reason"] : "The World theme is unavailable.",
  };
}

function parsedTheme(body: Record<string, unknown>): WorldThemeOutcome | null {
  const accentHue = body["accentHue"];
  const accentChroma = body["accentChroma"];
  const updatedAt = body["updatedAt"];
  if (typeof accentHue !== "number" || !Number.isFinite(accentHue)) return null;
  if (typeof accentChroma !== "number" || !Number.isFinite(accentChroma)) return null;
  if (updatedAt !== null && typeof updatedAt !== "number") return null;
  return { ok: true, accentHue, accentChroma, updatedAt };
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<WorldThemeOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  const theme = parsedTheme(body);
  return (
    theme ?? {
      ok: false,
      kind: "transport",
      reason: "The local Cleo server returned an invalid World theme.",
    }
  );
}

export async function readWorldTheme(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WorldThemeOutcome> {
  return call("/api/neucleos/world/theme", { worldKey }, fetchImpl);
}

export async function saveWorldTheme(
  worldKey: string,
  accentHue: number,
  accentChroma: number,
  fetchImpl: typeof fetch = fetch,
): Promise<WorldThemeOutcome> {
  return call("/api/neucleos/world/theme/save", { worldKey, accentHue, accentChroma }, fetchImpl);
}
